<!-- Logo Crop Modal -->
@if (showLogoCropper) {
  <app-image-cropper-modal
    [imageFile]="logoFileForCropping"
    [roundCropper]="true"
    (cropComplete)="onLogoCropComplete($event)"
    (cancel)="onLogoCropCancel()" />
}

<!-- Banner Crop Modal -->
@if (showBannerCropper) {
  <app-image-cropper-modal
    [imageFile]="bannerFileForCropping"
    [roundCropper]="false"
    [aspectRatio]="bannerAspectRatio"
    [resizeToWidth]="1200"
    cropDescription="Das Bild wird auf das Banner-Format (ca. 4:1) zugeschnitten – passend für Gutschein-PDF und Schenkwerk Webseite."
    (cropComplete)="onBannerCropComplete($event)"
    (cancel)="onBannerCropCancel()" />
}

<div class="space-y-6">
  @if (!isMember) {
    <!-- ══════════ Preview ══════════ -->
    <section id="section-brand-preview" hlmCard>
      <div hlmCardHeader>
        <h2 hlmCardTitle>So wirkt deine Marke</h2>
        <p hlmCardDescription>
          Name, Logo, Banner und Farben stellst du nur hier ein. Gutschein-App, Shopseite,
          Gutschein-PDFs und die Bezahlseite übernehmen sie automatisch.
        </p>
      </div>
      <div hlmCardContent>
        <app-brand-preview
          [name]="previewName"
          [logoUrl]="previewLogo"
          [bannerUrl]="previewBanner"
          [primaryColor]="brandingForm.get('voucher_app_primary_color')?.value"
          [textColor]="brandingForm.get('voucher_app_text_color')?.value" />
      </div>
    </section>

    <!-- ══════════ Brand name ══════════ -->
    <form [formGroup]="nameForm" (ngSubmit)="onSubmitName(nameSaveButton)">
      <section id="section-brand-name" hlmCard>
        <div hlmCardHeader>
          <h2 hlmCardTitle>Markenname</h2>
          <p hlmCardDescription>Der Name deines Geschäfts, wie Kunden ihn kennen.</p>
        </div>

        <div hlmCardContent>
          <div hlmField>
            <label hlmFieldLabel for="brand-name">Markenname *</label>
            <input
              hlmInput
              id="brand-name"
              type="text"
              formControlName="name"
              maxlength="100"
              placeholder="z. B. Café Morgenrot"
              [attr.aria-invalid]="isNameInvalid() ? 'true' : null" />
            @if (isNameInvalid()) {
              <p hlmFieldDescription class="text-destructive">Markenname ist erforderlich</p>
            }
            <app-brand-usage [places]="['storefront', 'pdf']" />
          </div>
        </div>

        <div hlmCardFooter class="justify-end border-t pt-4">
          <app-save-button
            #nameSaveButton
            size="sm"
            [disabled]="nameForm.invalid || !nameChanged"
            [loading]="nameLoading"
            (clickEvent)="onSubmitName(nameSaveButton)" />
        </div>
      </section>
    </form>
  }

  <!-- ══════════ Logo ══════════ -->
  <section id="section-logo" hlmCard>
    <div hlmCardHeader>
      <h2 hlmCardTitle>Logo</h2>
      <app-brand-usage [places]="isMember ? ['members'] : ['storefront', 'pdf']" />
    </div>

    <div hlmCardContent>
      @if (logoFile) {
        <!-- Cropped and waiting to be uploaded -->
        <div hlmItem variant="outline">
          <div hlmItemMedia variant="image">
            <img [src]="logoPreview" alt="Logo-Vorschau" class="rounded-full" />
          </div>
          <div hlmItemContent>
            <span hlmItemTitle>Logo bereit zum Speichern</span>
            <span hlmItemDescription>Das Logo wurde noch nicht hochgeladen.</span>
          </div>
          <div hlmItemActions class="gap-1">
            <button hlmBtn size="sm" [disabled]="logoUploadLoading" (click)="uploadLogo()">
              @if (logoUploadLoading) {
                <hlm-spinner />
                Wird gespeichert…
              } @else {
                Jetzt speichern
              }
            </button>
            <button
              hlmBtn
              variant="ghost"
              size="sm"
              [disabled]="logoUploadLoading"
              (click)="logoFile = null; logoPreview = null">
              Verwerfen
            </button>
          </div>
        </div>
      } @else if (currentLogoPath && confirmDeleteLogo) {
        <div hlmAlert variant="destructive">
          <ng-icon name="lucideTriangleAlert" />
          <p hlmAlertTitle>Logo wirklich löschen?</p>
          <p hlmAlertDescription>Das Logo wird dauerhaft entfernt.</p>
          <div class="mt-2 flex items-center gap-1">
            <button
              hlmBtn
              variant="destructive"
              size="sm"
              (click)="deleteLogo(); confirmDeleteLogo = false">
              Ja, löschen
            </button>
            <button hlmBtn variant="ghost" size="sm" (click)="confirmDeleteLogo = false">
              Abbrechen
            </button>
          </div>
        </div>
      } @else {
        <div class="flex items-center gap-4">
          @if (currentLogoPath) {
            <div class="relative shrink-0">
              <img
                [src]="getImageUrl(currentLogoPath)"
                alt="Aktuelles Logo"
                class="border-border size-20 rounded-full border object-cover" />
              <button
                hlmBtn
                variant="destructive"
                size="icon-xs"
                class="absolute -end-1 -top-1 rounded-full"
                aria-label="Logo löschen"
                (click)="confirmDeleteLogo = true">
                <ng-icon name="lucideX" />
              </button>
            </div>
          }

          @if (logoError) {
            <div hlmAlert variant="warning" class="mb-3">
              <ng-icon name="lucideTriangleAlert" />
              <p hlmAlertTitle>{{ logoError }}</p>
              <div hlmAlertAction>
                <button
                  hlmBtn
                  variant="ghost"
                  size="icon-sm"
                  aria-label="Schließen"
                  (click)="logoError = null">
                  <ng-icon name="lucideX" />
                </button>
              </div>
            </div>
          }

          <div class="space-y-2">
            <input
              type="file"
              accept="image/*"
              class="hidden"
              #logoFileInput
              (change)="onLogoFileSelected($event)" />
            <button hlmBtn variant="outline" size="sm" (click)="logoFileInput.click()">
              <ng-icon name="lucideImagePlus" />
              {{ currentLogoPath ? 'Logo ersetzen' : 'Logo auswählen' }}
            </button>
            <p hlmMuted class="text-xs">
              PNG, JPG oder WebP · max. 5 MB · wird kreisförmig zugeschnitten
            </p>
          </div>
        </div>
      }
    </div>
  </section>

  @if (!isMember) {
    <!-- ══════════ Banner ══════════ -->
    <section id="section-banner" hlmCard>
      <div hlmCardHeader>
        <h2 hlmCardTitle>Banner</h2>
        <app-brand-usage [places]="['storefront', 'pdf']" />
      </div>

      <div hlmCardContent>
        @if (bannerFile) {
          <div class="space-y-3">
            <img
              [src]="bannerPreview"
              alt="Banner-Vorschau"
              class="border-border h-28 w-full rounded-lg border object-cover" />
            <div class="flex items-center justify-between gap-2">
              <div>
                <p class="text-sm font-medium">Banner bereit zum Speichern</p>
                <p hlmMuted class="text-xs">Das Banner wurde noch nicht hochgeladen.</p>
              </div>
              <div class="flex items-center gap-1">
                <button hlmBtn size="sm" [disabled]="bannerUploadLoading" (click)="uploadBanner()">
                  @if (bannerUploadLoading) {
                    <hlm-spinner />
                    Wird gespeichert…
                  } @else {
                    Jetzt speichern
                  }
                </button>
                <button
                  hlmBtn
                  variant="ghost"
                  size="sm"
                  [disabled]="bannerUploadLoading"
                  (click)="bannerFile = null; bannerPreview = null">
                  Verwerfen
                </button>
              </div>
            </div>
          </div>
        } @else if (currentBannerPath && confirmDeleteBanner) {
          <div hlmAlert variant="destructive">
            <ng-icon name="lucideTriangleAlert" />
            <p hlmAlertTitle>Banner wirklich löschen?</p>
            <p hlmAlertDescription>Das Banner wird dauerhaft entfernt.</p>
            <div class="mt-2 flex items-center gap-1">
              <button
                hlmBtn
                variant="destructive"
                size="sm"
                (click)="deleteBanner(); confirmDeleteBanner = false">
                Ja, löschen
              </button>
              <button hlmBtn variant="ghost" size="sm" (click)="confirmDeleteBanner = false">
                Abbrechen
              </button>
            </div>
          </div>
        } @else {
          <div class="space-y-3">
            @if (currentBannerPath) {
              <div class="relative">
                <img
                  [src]="getImageUrl(currentBannerPath)"
                  alt="Aktuelles Banner"
                  class="border-border h-28 w-full rounded-lg border object-cover" />
                <button
                  hlmBtn
                  variant="destructive"
                  size="icon-xs"
                  class="absolute end-2 top-2 rounded-full"
                  aria-label="Banner löschen"
                  (click)="confirmDeleteBanner = true">
                  <ng-icon name="lucideX" />
                </button>
              </div>
            }

            @if (bannerError) {
              <div hlmAlert variant="warning" class="mb-3">
                <ng-icon name="lucideTriangleAlert" />
                <p hlmAlertTitle>{{ bannerError }}</p>
                <div hlmAlertAction>
                  <button
                    hlmBtn
                    variant="ghost"
                    size="icon-sm"
                    aria-label="Schließen"
                    (click)="bannerError = null">
                    <ng-icon name="lucideX" />
                  </button>
                </div>
              </div>
            }

            <input
              type="file"
              accept="image/*"
              class="hidden"
              #bannerFileInput
              (change)="onBannerFileSelected($event)" />
            <button hlmBtn variant="outline" size="sm" (click)="bannerFileInput.click()">
              <ng-icon name="lucideImagePlus" />
              {{ currentBannerPath ? 'Banner ersetzen' : 'Banner auswählen' }}
            </button>
            <p hlmMuted class="text-xs">PNG, JPG oder WebP · max. 5 MB · Seitenverhältnis 3:1</p>
          </div>
        }
      </div>
    </section>

    <form [formGroup]="brandingForm" class="space-y-6">
      <!-- ══════════ Colours ══════════ -->
      <section id="section-colors" hlmCard>
        <div hlmCardHeader>
          <h2 hlmCardTitle>Farben</h2>
          <p hlmCardDescription>
            Buttons, Akzente und Hervorhebungen überall dort, wo deine Marke erscheint.
          </p>
        </div>

        <div hlmCardContent class="grid gap-4 sm:grid-cols-2">
          <div hlmField>
            <label hlmFieldLabel for="primary-color">Hauptfarbe *</label>
            <hlm-input-group class="w-full">
              <hlm-input-group-addon>
                <input
                  type="color"
                  class="size-5 cursor-pointer rounded border-none bg-transparent p-0"
                  aria-label="Hauptfarbe wählen"
                  [value]="brandingForm.get('voucher_app_primary_color')?.value || '#000000'"
                  (input)="onColorPickerChange('voucher_app_primary_color', $event)" />
              </hlm-input-group-addon>
              <input
                hlmInputGroupInput
                id="primary-color"
                type="text"
                formControlName="voucher_app_primary_color"
                placeholder="#000000"
                [attr.aria-invalid]="isInvalid('voucher_app_primary_color') ? 'true' : null" />
            </hlm-input-group>
            @if (isInvalid('voucher_app_primary_color')) {
              <p hlmFieldDescription class="text-destructive">
                @if (brandingForm.get('voucher_app_primary_color')?.errors?.['required']) {
                  Hauptfarbe ist erforderlich
                } @else {
                  Gültige Hex-Farbe eingeben (z. B. #FF0000)
                }
              </p>
            } @else {
              <p hlmFieldDescription>Farbe für Buttons und Akzente.</p>
            }
            <app-brand-usage [places]="['app', 'storefront', 'pdf', 'checkout']" />
          </div>

          <div hlmField>
            <label hlmFieldLabel for="text-color">Schriftfarbe auf der Hauptfarbe *</label>
            <hlm-input-group class="w-full">
              <hlm-input-group-addon>
                <input
                  type="color"
                  class="size-5 cursor-pointer rounded border-none bg-transparent p-0"
                  aria-label="Schriftfarbe wählen"
                  [value]="brandingForm.get('voucher_app_text_color')?.value || '#FFFFFF'"
                  (input)="onColorPickerChange('voucher_app_text_color', $event)" />
              </hlm-input-group-addon>
              <input
                hlmInputGroupInput
                id="text-color"
                type="text"
                formControlName="voucher_app_text_color"
                placeholder="#FFFFFF"
                [attr.aria-invalid]="isInvalid('voucher_app_text_color') ? 'true' : null" />
            </hlm-input-group>
            @if (isInvalid('voucher_app_text_color')) {
              <p hlmFieldDescription class="text-destructive">
                @if (brandingForm.get('voucher_app_text_color')?.errors?.['required']) {
                  Schriftfarbe ist erforderlich
                } @else {
                  Gültige Hex-Farbe eingeben (z. B. #FFFFFF)
                }
              </p>
            } @else {
              <p hlmFieldDescription>Für Text auf Buttons und Flächen in der Hauptfarbe.</p>
            }
            <app-brand-usage [places]="['app', 'storefront']" />
          </div>

          <app-color-contrast-check
            class="sm:col-span-2"
            [background]="brandingForm.get('voucher_app_primary_color')?.value"
            [text]="brandingForm.get('voucher_app_text_color')?.value"
            (applySuggestion)="applyTextColor($event)" />
        </div>

        <div hlmCardFooter class="justify-end border-t pt-4">
          <app-save-button
            #designSaveButton
            size="sm"
            [disabled]="!brandingForm.valid || !formChanged"
            [loading]="loading"
            (clickEvent)="onSubmit(designSaveButton)" />
        </div>
      </section>
    </form>
  }
</div>
